/**
 * WorkflowCard — Renders one workflow within a careflow card.
 *
 * Displays the workflow name, time window, mute badge, validation alerts,
 * and nested escalation cards.
 *
 * Props:
 *   workflow             — workflow data object
 *   careflowTypeId       — parent careflow type (1, 3, 4, 5, 6)
 *   onEdit               — callback to open workflow edit drawer
 *   onDelete             — callback to open workflow delete dialog
 *   onAddEscalation      — callback to open add escalation drawer
 *   onEditEscalation     — callback (escalation, eIndex) to open edit escalation drawer
 *   expandedEscalations  — object mapping "workflowId-escalationId" → boolean
 *   onToggleEscalation   — callback (workflowId, escalationId) to toggle
 *   campusSettings       — campus settings for output visibility
 *   escMax               — max escalation period in seconds
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { convertSecto12Hour } from '../../../../libs.jsx';
import EscalationCard from './EscalationCard.jsx';

// Check if escalation periods cover the full 0–escMax range
function hasIncompletePeriodCoverage(escalations, escMax) {
    if (!escalations || escalations.length === 0) return true;

    const sorted = [...escalations].sort((a, b) => a.period_start - b.period_start);
    if (sorted[0].period_start > 0) return true;
    if (sorted[sorted.length - 1].period_end < escMax) return true;

    for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].period_end < sorted[i + 1].period_start) return true;
    }
    return false;
}

// Check if an escalation overlaps with others in the same workflow
function hasOverlappingEscalations(escalations, escalationId, start, end) {
    if (!escalations) return false;
    for (const esc of escalations) {
        if (esc.escalation_id === escalationId) continue;
        if (!(end <= esc.period_start || start >= esc.period_end)) return true;
    }
    return false;
}

/** Add Escalation button — disabled with popover when workflow is muted */
const AddEscalationButton = ({ isMuted, onClick }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);
    const timer = useRef(null);

    const show = useCallback(() => {
        if (!isMuted || !ref.current) return;
        clearTimeout(timer.current);
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < 80) {
            setStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 6}px`, left: `${rect.left + rect.width / 2}px`, transform: 'translateX(-50%)', top: 'auto', zIndex: 9999 });
        } else {
            setStyle({ position: 'fixed', top: `${rect.bottom + 6}px`, left: `${rect.left + rect.width / 2}px`, transform: 'translateX(-50%)', bottom: 'auto', zIndex: 9999 });
        }
        setOpen(true);
    }, [isMuted]);

    const hide = useCallback(() => {
        timer.current = setTimeout(() => setOpen(false), 100);
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    return (
        <div className="cf-workflow__footer">
            <span ref={ref} onMouseEnter={show} onMouseLeave={hide} style={{ position: 'relative', display: 'inline-block' }}>
                <button
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={isMuted ? undefined : onClick}
                    disabled={isMuted}
                    style={isMuted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                    <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Escalation
                </button>
                {open && isMuted ? (
                    <div className="na-type-popover" style={style}>
                        This workflow is muted. Unmute the workflow to add escalations.
                    </div>
                ) : null}
            </span>
        </div>
    );
};

const WorkflowCard = ({
    workflow,
    careflowTypeId,
    onEdit,
    onDelete,
    onAddEscalation,
    onEditEscalation,
    expandedEscalations,
    onToggleEscalation,
    campusSettings,
    escMax,
}) => {
    const isMuted = !!workflow.is_mute;
    const hasEscalations = workflow.escalations && workflow.escalations.length > 0;
    const incompletePeriods = hasEscalations && hasIncompletePeriodCoverage(workflow.escalations, escMax);

    // Determine border variant — skip danger styling when muted
    let borderClass = '';
    if (!isMuted && (!hasEscalations || incompletePeriods)) borderClass = ' cf-workflow--danger';

    return (
        <div className={`cf-workflow${borderClass}`}>
            {/* Validation alerts — suppress when workflow is muted */}
            {!isMuted && !hasEscalations ? (
                <div className="cf-card__alert cf-card__alert--danger">
                    <svg className="cf-card__alert-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                    No escalations set up on workflow
                </div>
            ) : null}
            {!isMuted && hasEscalations && incompletePeriods ? (
                <div className="cf-card__alert cf-card__alert--danger">
                    <svg className="cf-card__alert-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                    Escalation periods do not have full coverage
                </div>
            ) : null}

            {/* Header */}
            <div className="cf-workflow__header">
                <div>
                    <h3 className="cf-workflow__name">
                        {workflow.workflow_name}
                        {workflow.is_mute && careflowTypeId !== 1 ? (
                            <span className="cf-workflow__badge cf-workflow__badge--muted" style={{ marginLeft: '0.5rem' }}>
                                Muted
                            </span>
                        ) : null}
                    </h3>
                    <p className="cf-workflow__time">
                        {convertSecto12Hour(workflow.window_start)} – {convertSecto12Hour(workflow.window_end)}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
                        <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                        </svg>
                        Edit
                    </button>
                </div>
            </div>

            {/* Escalations */}
            {hasEscalations ? (
                <div className="cf-escalation-section">
                    <h4 className="cf-escalation-section__title">
                        <svg className="cf-workflow-section__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                        </svg>
                        Escalations
                    </h4>
                    {workflow.escalations.map((esc, eIndex) => {
                        const key = `${workflow.workflow_id}-${esc.escalation_id}`;
                        const overlap = hasOverlappingEscalations(
                            workflow.escalations, esc.escalation_id,
                            esc.period_start, esc.period_end
                        );
                        return (
                            <EscalationCard
                                key={esc.escalation_id || eIndex}
                                escalation={esc}
                                isExpanded={!!expandedEscalations[key]}
                                onToggle={() => onToggleEscalation(workflow.workflow_id, esc.escalation_id)}
                                onEdit={() => onEditEscalation(esc, eIndex)}
                                hasOverlap={overlap}
                                campusSettings={campusSettings}
                            />
                        );
                    })}
                </div>
            ) : null}

            {/* Add Escalation button */}
            <AddEscalationButton isMuted={isMuted} onClick={onAddEscalation} />
        </div>
    );
};

export default WorkflowCard;
